import React, { useState, useEffect } from "react";
import { MdTerminal } from "react-icons/md";
import { getLogs, subscribe } from "../utils/apiLogStore";

const LogsIcon = () => <MdTerminal size={22} aria-hidden="true" />;

const ChevronDown = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ChevronUp = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="18 15 12 9 6 15" />
  </svg>
);

const SIDEBAR_WIDTH = 56;
const PANEL_MARGIN = 12;
const COLLAPSED_WIDTH = 280;
const COLLAPSED_HEIGHT = 160;
const EXPANDED_HEIGHT_RATIO = 0.5;

function BackendLogsPanel({ open, onClose }) {
  const [logs, setLogs] = useState(getLogs);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    return subscribe(() => setLogs(getLogs()));
  }, []);

  const panelLeft = SIDEBAR_WIDTH + PANEL_MARGIN;
  const panelBottom = PANEL_MARGIN;
  const panelWidth = expanded ? "calc(50vw - 24px)" : COLLAPSED_WIDTH;
  const panelHeight = expanded
    ? `calc(${EXPANDED_HEIGHT_RATIO * 100}vh - ${PANEL_MARGIN * 2}px)`
    : COLLAPSED_HEIGHT;

  const panelStyle = {
    position: "fixed",
    left: panelLeft,
    bottom: panelBottom,
    width: panelWidth,
    height: panelHeight,
    maxHeight: expanded ? `calc(${EXPANDED_HEIGHT_RATIO * 100}vh - 24px)` : COLLAPSED_HEIGHT,
    backgroundColor: "rgba(15, 23, 42, 0.68)",
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
    borderRadius: "10px",
    border: "1px solid rgba(148, 163, 184, 0.18)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.25)",
    zIndex: 999,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    transition: "width 0.2s ease, height 0.2s ease",
  };

  const headerStyle = {
    padding: "10px 12px",
    borderBottom: "1px solid rgba(148, 163, 184, 0.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
    backgroundColor: "rgba(0, 0, 0, 0.12)",
  };

  const titleStyle = {
    color: "#e2e8f0",
    fontSize: "13px",
    fontWeight: "600",
    margin: 0,
  };

  const listStyle = {
    flex: 1,
    overflow: "auto",
    padding: "8px",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
    fontSize: "12px",
  };

  const entryStyle = (success) => ({
    marginBottom: "10px",
    padding: "10px",
    borderRadius: "6px",
    borderLeft: `3px solid ${success ? "#22c55e" : "#ef4444"}`,
    backgroundColor: success ? "rgba(34, 197, 94, 0.08)" : "rgba(239, 68, 68, 0.08)",
  });

  const lineStyle = { margin: "4px 0", color: "#cbd5e1", wordBreak: "break-all" };
  const labelStyle = { color: "#94a3b8", marginRight: "6px" };
  const successStyle = { color: "#22c55e", fontWeight: "600" };
  const errorStyle = { color: "#f87171", fontWeight: "600" };
  const envStyle = (env) => ({
    display: "inline-block",
    padding: "2px 6px",
    borderRadius: "4px",
    fontSize: "10px",
    fontWeight: "600",
    marginLeft: "6px",
    backgroundColor: env === "prod" ? "rgba(34, 197, 94, 0.25)" : "rgba(59, 130, 246, 0.25)",
    color: env === "prod" ? "#86efac" : "#93c5fd",
  });

  if (!open) return null;

  return (
        <div style={panelStyle}>
          <div style={headerStyle}>
            <h2 style={titleStyle}>Admin Backend Logs</h2>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#94a3b8",
                  cursor: "pointer",
                  padding: "4px",
                }}
                title={expanded ? "Shrink" : "Expand to half screen"}
                aria-label={expanded ? "Shrink panel" : "Expand panel"}
              >
                {expanded ? <ChevronDown /> : <ChevronUp />}
              </button>
              <button
                type="button"
                onClick={onClose}
                style={{
                  background: "none",
                  border: "none",
                  color: "#94a3b8",
                  cursor: "pointer",
                  padding: "4px",
                  fontSize: "18px",
                  lineHeight: 1,
                }}
                title="Close"
                aria-label="Close"
              >
                ×
              </button>
            </div>
          </div>
          <div style={listStyle}>
            {(() => {
              const filtered = logs.filter(
                (entry) =>
                  !(
                    entry.success &&
                    (entry.method || "").toUpperCase() === "GET" &&
                    (entry.endpoint === "/agents/library" || entry.endpoint?.startsWith?.("/agents/library?"))
                  )
              );
              return filtered.length === 0 ? (
                <p style={{ color: "#64748b", margin: 0 }}>No API calls yet. Actions you take will appear here.</p>
              ) : (
              filtered.map((entry) => (
                <div key={entry.id} style={entryStyle(entry.success)}>
                  <div style={lineStyle}>
                    <span style={entry.success ? successStyle : errorStyle}>
                      {entry.success ? "✓ Success" : "✗ Failed"}
                    </span>
                    <span style={envStyle(entry.env)}>{entry.env === "prod" ? "Prod" : entry.env === "dev" ? "Dev" : entry.env}</span>
                  </div>
                  <div style={lineStyle}>
                    <span style={labelStyle}>Endpoint:</span>
                    <span style={{ color: "#e2e8f0" }}>{entry.method} {entry.endpoint}</span>
                  </div>
                  <div style={lineStyle}>
                    <span style={labelStyle}>Message:</span>
                    <span style={entry.success ? { color: "#a7f3d0" } : { color: "#fca5a5" }}>{entry.message}</span>
                  </div>
                  {entry.payload != null && (typeof entry.payload !== "object" || Object.keys(entry.payload).length > 0) && (
                    <div style={lineStyle}>
                      <span style={labelStyle}>Payload sent:</span>
                      <pre style={{ margin: "4px 0 0", fontSize: "11px", color: "#94a3b8", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                        {typeof entry.payload === "object" ? JSON.stringify(entry.payload, null, 2) : String(entry.payload)}
                      </pre>
                    </div>
                  )}
                  {entry.responsePreview && expanded && (
                    <div style={{ ...lineStyle, marginTop: "6px" }}>
                      <span style={labelStyle}>Response:</span>
                      <pre style={{ margin: "4px 0 0", fontSize: "11px", color: "#94a3b8", whiteSpace: "pre-wrap", wordBreak: "break-all", maxHeight: "120px", overflow: "auto" }}>
                        {entry.responsePreview}
                      </pre>
                    </div>
                  )}
                </div>
              ))
            );
            })()}
          </div>
        </div>
  );
}

export default BackendLogsPanel;
export { LogsIcon };
